import { NgTemplateOutlet, AsyncPipe } from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  Input,
  ViewEncapsulation,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CountdownTypes } from '@modules/countdown/enums/countdown-types.enum';
import { MediaStreamModel } from '@modules/media/models/media-stream.model';
import { MediaType } from '@modules/media/models/media.types';
import { PlayerService } from '@modules/media/service/player.service';
import { EmbedCodeComponent } from '@modules/shared/modal/embed-code/embed-code.component';
import { DialogService } from '@modules/shared/services/dialog.service';

import { StreamCardComponent } from '../../card/stream-card/stream-card.component';
import { ItemSliderComponent } from '../../slider/item-slider/item-slider.component';
import { StageSectionItemInterface } from '../models/stage-section.model';

import { StageItemComponent } from './stage-item/stage-item.component';

@Component({
  selector: 'mb-stage-section',
  templateUrl: './stage-section.component.html',
  styleUrls: ['./stage-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [NgTemplateOutlet, ItemSliderComponent, StageItemComponent, StreamCardComponent, AsyncPipe, TranslatePipe],
})
export class StageSectionComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private playerService = inject(PlayerService);
  private dialogService = inject(DialogService);
  private cdr = inject(ChangeDetectorRef);

  private destroy$ = new Subject<boolean>();
  public mediaType = MediaType;
  public slideIndex = 0;
  public activeStream$ = new BehaviorSubject<string | null>(null);
  public activeStageItem: MediaStreamModel | null = null;
  public hasEmbedCode = false;
  public countdownTypes = CountdownTypes;
  public hasBottomSpace = false;

  private _items: StageSectionItemInterface[] = [];
  @Input()
  set items(v: StageSectionItemInterface[]) {
    this._items = v;
    this.hasBottomSpace = false;
    v.forEach((item) => {
      const media = item.media as MediaStreamModel;
      if (media?.showEmbedCode || media?.showIcsDownload) {
        this.hasBottomSpace = true;
      }
    });
  }
  get items() {
    return this._items;
  }
  @Input() isMainStage = false;

  private updateActiveStream(videoId: string | null) {
    /**
     * if stream is present in the stage, set the slideIndex, so the slider
     * jumps to the previously active stream when the player is closed
     */
    if (videoId) {
      const index = this.items.findIndex((item) => item.media.id === videoId);
      if (index) {
        this.slideIndex = index;
      }
    }
    this.activeStream$.next(videoId || null);
  }

  public setSlideIndex(index: number) {
    this.activeStageItem = null;
    if (this.items[index].media?.type === MediaType.STREAM) {
      this.activeStageItem = this.items[index].media as MediaStreamModel;
      this.cdr.detectChanges();
    }
    this.slideIndex = index;
  }

  public openEmbedOverlay(streamCardId: string) {
    this.dialogService.open({
      component: EmbedCodeComponent,
      disableCloseBtn: true,
      inputs: {
        streamCardId: streamCardId,
      },
    });
  }

  ngOnInit() {
    if (this.items.length >= 1 && this.items[0].media.type === MediaType.STREAM) {
      this.activeStageItem = this.items[0].media as MediaStreamModel;
    }
    // stage should hide by setting an active stream if stage is the main stage
    if (this.isMainStage) {
      this.updateActiveStream(this.route.snapshot.queryParams.video || null);
      this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((data) => {
        this.updateActiveStream(data.video || null);
      });

      this.playerService.playerClosed$.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.updateActiveStream(null);
      });
    }
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
